import { Button, Icon, Tab, Tabs } from '@blueprintjs/core';
import useAssistantChat from 'ai-assistant/hooks/useAssistantChat';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import './AssistantFrame.light.scss';
import { AnimatePresence, motion, Transition } from 'framer-motion';
import { AssistantChatContent } from '../assistant-chat-content/AssistantChatContent';
import { AssistantHistory } from '../assistant-history/AssistantHistory';
import { useIsSmallScreen } from 'hooks/useIsSmallScreen';
import { selectUser } from 'controllers/user/action';

const easingTransition: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 30
}

const BASE_OPEN_HEIGHT = 520;
const MAX_OPEN_HEIGHT = 680;
const BASE_COMPOSER_INPUT_HEIGHT = 52;

export interface AssistantFrameRef {
  close: () => void;
}

const logAnalyticsEvent = (_event: { category: string; action: string; label?: string }) => {
  // TODO
};

export const AssistantFrame = forwardRef<AssistantFrameRef>((_props, ref) => {
  const chat = useAssistantChat();
  const { isOpen, toggleAssistant, isModelPage } = chat;
  const currentModelKey = chat.currentModelAssociation?.modelKey ?? null;
  const isSmallScreen = useIsSmallScreen();
  const user = useSelector(selectUser);
  const isLoggedIn = Boolean(user);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showChatContent, setShowChatContent] = useState(false);
  const [activeTabId, setActiveTabId] = useState<'current' | 'history'>('current');
  const [embedTarget, setEmbedTarget] = useState<HTMLElement | null>(null);
  const [composerInputHeight, setComposerInputHeight] = useState(BASE_COMPOSER_INPUT_HEIGHT);
  const previousOpenStateRef = useRef<boolean | null>(null);
  const previousFullScreenStateRef = useRef<boolean | null>(null);
  const wasEmbeddedRef = useRef(false);

  // Guests have no chat history, so keep them on the current chat only.
  useEffect(() => {
    if (!isLoggedIn && activeTabId !== 'current') {
      setActiveTabId('current');
    }
  }, [isLoggedIn, activeTabId]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!isModelPage) {
      setEmbedTarget(null);
      return;
    }

    setEmbedTarget(document.getElementById('assistant-embed-root'));
  }, [isModelPage]);

  useEffect(() => {
    if (isModelPage && currentModelKey) {
      setActiveTabId('current');
    }
  }, [currentModelKey, isModelPage]);

  const isEmbedded = Boolean(embedTarget);
  const isPanelOpen = isEmbedded || isOpen;

  useEffect(() => {
    const wasEmbedded = wasEmbeddedRef.current;

    if (isEmbedded && !wasEmbedded) {
      previousOpenStateRef.current = isOpen;
      previousFullScreenStateRef.current = isFullScreen;

      if (!isOpen) {
        toggleAssistant(true);
      }

      if (isFullScreen) {
        setIsFullScreen(false);
      }
    }

    if (!isEmbedded && wasEmbedded) {
      if (previousOpenStateRef.current !== null && previousOpenStateRef.current !== isOpen) {
        toggleAssistant(previousOpenStateRef.current);
      }

      if (previousFullScreenStateRef.current !== null && previousFullScreenStateRef.current !== isFullScreen) {
        setIsFullScreen(previousFullScreenStateRef.current);
      }

      previousOpenStateRef.current = null;
      previousFullScreenStateRef.current = null;
    }

    wasEmbeddedRef.current = isEmbedded;
  }, [isEmbedded, isOpen, isFullScreen, toggleAssistant]);

  useImperativeHandle(ref, () => ({
    close: () => {
      toggleAssistant(false);
    }
  }));

  useEffect(() => {
    if (!isOpen) {
      setShowChatContent(false);
      setIsFullScreen(false);
      setActiveTabId('current');
      setComposerInputHeight(BASE_COMPOSER_INPUT_HEIGHT);
    } else {
      chat.coordinator.withNextRender(() => {
        setShowChatContent(true);
      })
    }
  }, [isOpen, chat.coordinator]);

  const handleComposerHeightChange = useCallback((height: number) => {
    setComposerInputHeight(current => {
      if (!Number.isFinite(height)) {
        return current;
      }

      const next = Math.max(BASE_COMPOSER_INPUT_HEIGHT, Math.round(height));
      return current === next ? current : next;
    });
  }, []);

  const expandedOpenHeight = Math.min(
    MAX_OPEN_HEIGHT,
    BASE_OPEN_HEIGHT + Math.max(0, composerInputHeight - BASE_COMPOSER_INPUT_HEIGHT),
  );

  const handleOpenAIAssistant = () => {
    toggleAssistant(true);
    logAnalyticsEvent({ category: 'AI Assistant', action: 'opened_ai_assistant' });
  }

  const handleCloseAIAssistant = () => {
    toggleAssistant(false);
    logAnalyticsEvent({ category: 'AI Assistant', action: 'closed_ai_assistant' });
  }

  const frameHeader = (
    <>
      <div className='assistant-frame__header-left'>
        <div className="assistant-frame__header-text">
          <p className='assistant-frame__header-title'>AI Assistant</p>
          <p className='assistant-frame__header-beta'>beta</p>
        </div>
      </div>

      <div className='assistant-frame__header-right'>
        <Button
          outlined
          intent="primary"
          icon="plus"
          className='assistant-frame__new-chat-button'
          onClick={chat.handleReset}
        >
          New Chat
        </Button>
        <div className='assistant-frame__header-divider' aria-hidden />
        <Button
          minimal
          className='assistant-frame__icon-button'
          intent="primary"
          icon={<Icon icon={isFullScreen ? 'minimize' : 'maximize'} size={20} />}
          onClick={() => setIsFullScreen(!isFullScreen)}
        />
        {!isEmbedded && (
          <Button
            minimal
            className='assistant-frame__icon-button'
            intent="primary"
            icon={<Icon icon='cross' size={20} />}
            onClick={handleCloseAIAssistant}
          />
        )}
      </div>
    </>
  );

  const frameBody = (
    <>
      {isLoggedIn && (
        <Tabs
          id="assistant-frame-tabs"
          selectedTabId={activeTabId}
          onChange={(nextId) => setActiveTabId(nextId as 'current' | 'history')}
          renderActiveTabPanelOnly
          className='assistant-frame__tabs'
        >
          <Tab id='current' title='Current Chat' />
          <Tab id='history' title='History' />
        </Tabs>
      )}

      <div className='assistant-frame__tab-panel'>
        {(!isLoggedIn || activeTabId === 'current') && showChatContent && (
          <AssistantChatContent
            chat={chat}
            isSmallScreen={isSmallScreen}
            onComposerHeightChange={handleComposerHeightChange}
          />
        )}
        {isLoggedIn && activeTabId === 'history' && (
          <AssistantHistory
            chat={chat}
            onSessionResume={() => setActiveTabId('current')}
          />
        )}
      </div>
    </>
  );

  // Docked model-page frame: no framer-motion. The dock already owns layout, and
  // entrance springs were competing with Plotly paint when resuming models with results.
  if (isEmbedded) {
    const embeddedFrame = (
      <div
        className={`assistant-frame-container assistant-frame-container--embedded${isFullScreen ? ' assistant-frame-container--embedded-fullscreen' : ''}`}
      >
        <div
          className={`assistant-frame assistant-frame--embedded${isFullScreen || (isSmallScreen && isPanelOpen) ? ' assistant-frame--fullscreen' : ''}`}
        >
          <div className='assistant-frame__chat'>
            <div className='assistant-frame__header'>
              {frameHeader}
            </div>
            {frameBody}
          </div>
        </div>
      </div>
    );

    return createPortal(embeddedFrame, embedTarget!);
  }

  return (
    <motion.div
      className='assistant-frame-container'
      initial={{
        bottom: '2rem',
        right: '2rem',
        height: 'auto',
        width: 'auto'
      }}
      animate={{
        bottom: isSmallScreen && (isOpen || isFullScreen) ? '0rem' : '2rem',
        right: isSmallScreen && (isOpen || isFullScreen) ? '0rem' : '2rem'
      }}
      transition={easingTransition}
    >
      <motion.div
        className={`assistant-frame${isFullScreen || (isSmallScreen && isPanelOpen) ? ' assistant-frame--fullscreen' : ''}`}
        animate={{
          width: isFullScreen
            ? (isSmallScreen ? 'calc(100vw - 6px)' : 'calc(100vw - 4rem)')
            : isOpen
              ? (isSmallScreen ? 'calc(100vw - 6px)' : '800px')
              : 'auto',
          height: isFullScreen
            ? (isSmallScreen ? 'calc(100vh - 6px)' : 'calc(100vh - 4rem)')
            : isOpen
              ? (isSmallScreen ? '67vh' : `${expandedOpenHeight}px`)
              : 'auto',
          maxWidth: isSmallScreen ? 'none' : 'calc(100vw - 4rem)',
          maxHeight: isSmallScreen ? 'none' : 'calc(100vh - 4rem)',
        }}
        transition={easingTransition}
      >
        <AnimatePresence>
          {isPanelOpen ? (
            <motion.div
              className='assistant-frame__chat'
              initial={{ opacity: 0, scale: 0, transformOrigin: 'bottom right' }}
              animate={{ opacity: 1, scale: 1, transformOrigin: 'bottom right' }}
              exit={{ opacity: 0, scale: 0, transformOrigin: 'bottom right' }}
              transition={easingTransition}
            >
              <motion.div
                className='assistant-frame__header'
                initial={{ translateY: "-100%" }}
                animate={{ translateY: "0%" }}
                exit={{ translateY: "-100%" }}
                transition={{
                  type: 'spring',
                  stiffness: 200,
                  damping: 30,
                  delay: 0.15
                }}
              >
                {frameHeader}
              </motion.div>
              {frameBody}
            </motion.div>
          ) : (
            <div className='assistant-frame__button' onClick={handleOpenAIAssistant}>
              <Icon
                icon='chat'
                size={20}
              />
              <h2 className='assistant-frame__title'>ESET AI</h2>
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
});

export default AssistantFrame;
